import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Typography,
  CircularProgress,
  Grid,
  Chip,
  Stack
} from '@mui/material';
import { Delete, Add, Edit, Close as CloseIcon } from '@mui/icons-material';
import { investmentAPI } from '../../api/investmentAPI';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Watchlist } from '../../types/investment';
import { useAuth } from '../../contexts/AuthContext';
import { TimeHorizon } from './StockList';
import TickerDetailView from '../TickerDetailView';
import { EnhancedWatchlistTable } from './EnhancedWatchlistTable';
import { useProfile } from '../../contexts/ProfileContext';
import { useLoadingStatus } from '../../contexts/LoadingStatusContext';
import { useMemo } from 'react';

interface Props {
  profileId: string;
  profile: {
    id: string;
    name: string;
    investment_profile_template: string;
    created_at: string;
    updated_at: string;
    watchlists: Watchlist[];
    screeners: any[];
  };
}

// Define an interface for ticker data structure
interface TickerDataItem {
  ticker: string;
  name?: string;
  close?: number;
  volume?: number;
  // Add other fields as needed
  [key: string]: any; // Allow for other dynamic properties
}

export const WatchlistManager: React.FC<Props> = ({
  profileId,
  profile,
}) => {
  const [watchlists, setWatchlists] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState('');
  const [tickerInput, setTickerInput] = useState('');
  const [selectedWatchlist, setSelectedWatchlist] = useState<Watchlist | null>(null);
  const [selectedTicker, setSelectedTicker] = useState<string | null>(null);
  const [tickerData, setTickerData] = useState<Record<string, TickerDataItem>>({});
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [watchlistToDelete, setWatchlistToDelete] = useState<string | null>(null);
  const [newWatchlistTickers, setNewWatchlistTickers] = useState('');
  const [editWatchlistName, setEditWatchlistName] = useState('');
  const [editWatchlistTickers, setEditWatchlistTickers] = useState('');
  const [tickerToRemove, setTickerToRemove] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [tickerDetailData, setTickerDetailData] = useState<any>(null);
  const [watchlistTimeHorizons, setWatchlistTimeHorizons] = useState<Record<string, 'Short Term' | 'Mid Term' | 'Long Term'>>({});
  const [validTickers, setValidTickers] = useState<string[]>([]);
  const [invalidTickers, setInvalidTickers] = useState<string[]>([]);
  const [isValidatingTickers, setIsValidatingTickers] = useState(false);
  const [watchlistColumnPreferences, setWatchlistColumnPreferences] = useState<Record<string, string>>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  
  // Use auth context
  const { getAuthHeaders } = useAuth();
  const { 
    profiles, 
    currentProfile, 
    currentProfileId, 
    refreshProfiles, 
    handleProfileSelect 
  } = useProfile();  

  const { addStatus, removeStatus } = useLoadingStatus();
  const api = useMemo(() => investmentAPI(addStatus, removeStatus), []);

  // Fetch ticker data for all tickers in watchlists
  const fetchTickerData = useCallback(async (tickers: string[]) => {
    if (tickers.length === 0) return;

    const uniqueTickers = [...new Set(tickers)];
    let newTickerData: Record<string, TickerDataItem> = {};
    try {
      newTickerData = await api.getMultiTickerData(uniqueTickers, currentProfile?.investment_profile_template || null);
    } catch (err) {
      console.error('Error fetching ticker data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch ticker data');
    }
    setTickerData((prev: Record<string, TickerDataItem>) => ({...prev, ...newTickerData}));
  }, [api, currentProfile]);

  // Fetch watchlists
  useEffect(() => {
    const fetchWatchlists = async () => {
      try {
        setLoading(true);
        const data = await api.getWatchlists(profileId);
        setWatchlists(data);
        if (data && data.length > 0) {
          const allTickers = [...new Set(data.flatMap(w => w.tickers))];
          await fetchTickerData(allTickers);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch watchlists');
      } finally {
        setLoading(false);
      }
    };

    fetchWatchlists();
  }, [profileId, api, fetchTickerData]);

  // Fetch valid tickers on component mount
  useEffect(() => {
    const fetchValidTickers = async () => {
      try {
        const allowedTickers = await api.getAllTickersAllowed();
        // Assuming the API returns an array of strings or objects with a ticker property
        let tickerList: string[] = [];
        
        if (Array.isArray(allowedTickers)) {
          tickerList = typeof allowedTickers[0] === 'string' 
            ? allowedTickers 
            : allowedTickers.map((t: any) => t.ticker || '').filter(Boolean);
        }
        
        console.log(`Loaded ${tickerList.length} valid tickers`);
        setValidTickers(tickerList.map(t => t.toUpperCase()));
      } catch (err) {
        console.error('Error fetching valid tickers:', err);
        // Don't show an error to the user, just log it - we'll do individual validation later
      }
    };

    fetchValidTickers();
  }, [api]);

  // Add a function to validate tickers
  const validateTickers = (tickerString: string): { valid: string[], invalid: string[] } => {
    const inputTickers = tickerString.toUpperCase().split(',')
      .map(t => t.trim())
      .filter(Boolean);
    
    if (validTickers.length === 0) {
      // If we couldn't load the valid tickers list, we can't validate
      // Just assume all tickers are valid for now to not block the user
      return { valid: inputTickers, invalid: [] };
    }
    
    const valid: string[] = [];
    const invalid: string[] = [];
    
    inputTickers.forEach(ticker => {
      if (validTickers.includes(ticker)) {
        valid.push(ticker);
      } else {
        invalid.push(ticker);
      }
    });
    
    return { valid, invalid };
  };

  // Reset errors when edit dialog closes
  useEffect(() => {
    if (!isEditDialogOpen) {
      setInvalidTickers([]);
      setEditError(null);
    }
  }, [isEditDialogOpen]);

  // Create new watchlist
  const handleCreateWatchlist = async () => {
    // Set the flag to indicate submission was attempted
    setHasAttemptedSubmit(true);
    
    try {
      if (!newWatchlistName.trim()) {
        setCreateError('Please enter a watchlist name');
        return;
      }

      if (!newWatchlistTickers.trim()) {
        setCreateError('Please enter at least one ticker');
        return;
      }

      setIsValidatingTickers(true);
      
      // Parse ticker input - clean up the input to handle various formats
      const tickerInput = newWatchlistTickers
        .toUpperCase()
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);
      
      if (tickerInput.length === 0) {
        setCreateError('Please enter at least one valid ticker');
        setIsValidatingTickers(false);
        return;
      }

      // Simple validation to check format before API call
      const invalidFormatTickers = tickerInput.filter(t => !/^[A-Z0-9.]{1,10}$/.test(t));
      if (invalidFormatTickers.length > 0) {
        setInvalidTickers(invalidFormatTickers);
        setCreateError(`Invalid ticker format: ${invalidFormatTickers.join(', ')}`);
        setIsValidatingTickers(false);
        return;
      }
      
      // Now send to proper validation if needed
      if (validTickers.length > 0) {
        const { valid, invalid } = validateTickers(newWatchlistTickers);
        
        if (invalid.length > 0) {
          setInvalidTickers(invalid);
          setCreateError(`Invalid ticker(s): ${invalid.join(', ')}. Please remove or correct these tickers.`);
          setIsValidatingTickers(false);
          return;
        }
        
        if (valid.length === 0) {
          setCreateError('Please enter at least one valid ticker');
          setIsValidatingTickers(false);
          return;
        }
        
        // Use the validated tickers
        tickerInput.length = 0;
        tickerInput.push(...valid);
      }

      setLoading(true);
      console.log('Creating watchlist with:', {
        profile_id: profileId,
        name: newWatchlistName,
        tickers: tickerInput
      });

      try {
        const newWatchlist = await api.createWatchlist({
          profile_id: profileId,
          name: newWatchlistName,
          tickers: tickerInput
        });

        console.log('Received new watchlist from API:', newWatchlist);
        
        // Make sure the created watchlist has the expected structure with an explicit name
        const safeWatchlist = {
          ...newWatchlist,
          id: newWatchlist.id || `temp-${Date.now()}`,
          name: newWatchlistName, // Explicitly use the name from the input
          tickers: newWatchlist.tickers || tickerInput
        };
        
        // Log the final watchlist object being added to state
        console.log('Adding watchlist to state:', safeWatchlist);
        
        // CHANGED: Add new watchlist at the beginning of the array instead of the end
        setWatchlists(prev => [safeWatchlist, ...prev]);
        
        await fetchTickerData(tickerInput);
        
        // Reset state on success
        setIsCreateDialogOpen(false);
        setNewWatchlistName('');
        setNewWatchlistTickers('');
        setCreateError(null);
        setInvalidTickers([]);
        setHasAttemptedSubmit(false);
      } catch (apiError: any) {
        console.error('API error when creating watchlist:', apiError);
        setCreateError(apiError?.message || 'Failed to create watchlist. Please try again.');
      }
    } catch (err) {
      console.error('Error in handleCreateWatchlist:', err);
      setCreateError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
      setIsValidatingTickers(false);
    }
  };

  // Open edit dialog
  const handleOpenEditDialog = (watchlist: Watchlist) => {
    console.log('Opening edit dialog for watchlist:', watchlist);
    
    // Reset all error states and invalid tickers when opening the dialog
    setEditError(null);
    setInvalidTickers([]);
    
    // Make sure we're setting the correct watchlist
    setSelectedWatchlist(watchlist);
    
    // Ensure we use the watchlist name and tickers with fallbacks
    const watchlistName = watchlist.name || '';
    setEditWatchlistName(watchlistName);
    
    const tickersList = Array.isArray(watchlist.tickers) ? watchlist.tickers : [];
    setEditWatchlistTickers(tickersList.join(', '));
    
    // Open the dialog
    setIsEditDialogOpen(true);
  };

  // Update watchlist
  const handleUpdateWatchlist = async () => {
    if (!selectedWatchlist) {
      console.error('No watchlist selected for update');
      return;
    }
    
    try {
      // Clear any existing errors before validation
      setEditError(null);
      setInvalidTickers([]);
      
      // Get and clean ticker input
      const tickerInputRaw = editWatchlistTickers || '';
      const tickerArray = tickerInputRaw
        .split(',')
        .map(t => t.trim())
        .filter(Boolean)
        .map(t => t.toUpperCase());
      
      // Get watchlist name, using original if empty
      let nameInput = editWatchlistName?.trim() || '';
      if (!nameInput && selectedWatchlist.name) {
        nameInput = selectedWatchlist.name;
      }
      
      // Validate inputs
      if (!nameInput) {
        setEditError('Please enter a watchlist name');
        return;
      }
      
      if (tickerArray.length === 0) {
        setEditError('Please enter at least one ticker');
        return;
      }
      
      setIsValidatingTickers(true);
      console.log('Validating tickers:', tickerArray);
      
      // Only validate if we have our validation list loaded
      let validTickerArray = tickerArray;
      if (validTickers.length > 0) {
        const { valid, invalid } = validateTickers(tickerInputRaw);
        
        if (invalid.length > 0) {
          setInvalidTickers(invalid);
          setEditError(`Invalid ticker(s): ${invalid.join(', ')}. Please remove or correct these tickers.`);
          setIsValidatingTickers(false);
          return;
        }
        
        if (valid.length === 0) {
          setEditError('Please enter at least one valid ticker');
          setIsValidatingTickers(false);
          return;
        }
        
        validTickerArray = valid;
      }

      setLoading(true);
      
      // Create update payload - REMOVED created_at property
      const updatePayload = {
        id: selectedWatchlist.id,
        profile_id: profileId,
        name: nameInput,
        tickers: validTickerArray,
        favorite: selectedWatchlist.favorite || false
      };

      // Log the request payload for debugging
      console.log('Updating watchlist with request:', updatePayload);

      try {
        const updatedWatchlist = await api.updateWatchlist(updatePayload);
        
        console.log('Received updated watchlist:', updatedWatchlist);
        
        // Create a safe object that combines API response with our known good data
        const safeUpdatedWatchlist = {
          ...selectedWatchlist,
          ...updatedWatchlist,
          id: selectedWatchlist.id, // Keep the original ID
          name: nameInput, // Use our input name
          tickers: validTickerArray // Use our validated tickers
        };
        
        // Update the watchlists array with the new data
        setWatchlists(prev => {
          // Find and replace the updated watchlist
          const updated = prev.map(w => 
            w.id === selectedWatchlist.id ? safeUpdatedWatchlist : w
          );
          console.log('Updated watchlists state:', updated);
          return updated;
        });

        // Fetch data for any tickers that might be new
        await fetchTickerData(validTickerArray);
        
        // Reset UI state on success
        setIsEditDialogOpen(false);
        setSelectedWatchlist(null);
        setEditWatchlistName('');
        setEditWatchlistTickers('');
        setEditError(null);
        setInvalidTickers([]);
      } catch (apiError: any) {
        // Enhanced API error handling
        console.error('API error in updateWatchlist:', apiError);
        
        let errorMessage = 'Failed to update watchlist';
        
        // Extract API response details if available
        if (apiError.response) {
          console.error('API response error:', apiError.response);
          errorMessage = `Server error: ${apiError.response.status} - ${apiError.response.statusText || 'Unknown error'}`;
          
          if (apiError.response.data) {
            console.error('API error details:', apiError.response.data);
            if (typeof apiError.response.data === 'string') {
              errorMessage = apiError.response.data;
            } else if (apiError.response.data.message) {
              errorMessage = apiError.response.data.message;
            } else if (apiError.response.data.error) {
              errorMessage = apiError.response.data.error;
            }
          }
        } else if (apiError.message) {
          errorMessage = apiError.message;
        }
        
        // Show error to user
        setEditError(`${errorMessage} - Please try again.`);
      }
    } catch (err) {
      console.error('Update watchlist error:', err);
      setEditError(err instanceof Error ? err.message : 'Failed to update watchlist');
    } finally {
      setLoading(false);
      setIsValidatingTickers(false);
    }
  };

  // Remove ticker from edit view
  const handleRemoveTicker = (ticker: string) => {
    console.log(`Removing ticker: ${ticker}`);
    
    // Clear any existing errors
    setEditError(null);
    setInvalidTickers([]);
    
    if (!editWatchlistTickers) {
      setEditWatchlistTickers('');
      return;
    }

    // Split the input, ensuring we properly handle whitespace
    const currentTickers = editWatchlistTickers.split(',')
      .map(t => t?.trim() || '')
      .filter(Boolean);
    
    console.log('Current tickers before removing:', currentTickers);
    
    // Filter out the ticker to remove
    const filteredTickers = currentTickers.filter(t => 
      t.toUpperCase() !== ticker.toUpperCase()
    );
    
    console.log('Tickers after removing:', filteredTickers);
    
    if (filteredTickers.length === 0) {
      setEditWatchlistTickers('');
      setEditError('Please enter at least one ticker');
      return;
    }
    
    // Join with comma and space for readability
    const newTickerString = filteredTickers.join(', ');
    console.log('New ticker string:', newTickerString);
    setEditWatchlistTickers(newTickerString);
  };

  // Show ticker details
  const handleTickerSelect = async (ticker: string) => {
    try {
      const data = await api.getTickerData(ticker, currentProfile?.investment_profile_template || null);
      setTickerDetailData(data);
      setSelectedTicker(ticker);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch ticker data');
    }
  };

  // Delete watchlist
  const handleDeleteWatchlist = async () => {
    if (!watchlistToDelete) return;

    try {
      setLoading(true);
      await api.deleteWatchlist(profileId, watchlistToDelete);
      setWatchlists(watchlists.filter(w => w.id !== watchlistToDelete));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete watchlist');
    } finally {
      setLoading(false);
      setConfirmDeleteDialogOpen(false);
      setWatchlistToDelete(null);
    }
  };

  const getActions = (watchlist: any) => [
    {
      icon: <Edit />,
      label: 'Edit',
      onClick: () => handleOpenEditDialog(watchlist)
    },
    {
      icon: <Delete />,
      label: 'Delete',
      onClick: () => {
        setWatchlistToDelete(watchlist.id);
        setConfirmDeleteDialogOpen(true);
      }
    }
  ];

  // Add a function to load preferences from localStorage
  const loadPreferences = useCallback(() => {
    try {
      // Load column preferences
      const savedColumnPrefs = localStorage.getItem(`watchlist_column_prefs_${profileId}`);
      if (savedColumnPrefs) {
        setWatchlistColumnPreferences(JSON.parse(savedColumnPrefs));
      }
      
      // Load time horizon preferences
      const savedTimeHorizons = localStorage.getItem(`watchlist_time_horizons_${profileId}`);
      if (savedTimeHorizons) {
        setWatchlistTimeHorizons(JSON.parse(savedTimeHorizons));
      }
    } catch (err) {
      console.error('Error loading watchlist preferences:', err);
    }
  }, [profileId]);

  // Add a function to save preferences to localStorage
  const saveColumnPreference = useCallback((watchlistId: string, presetView: string) => {
    try {
      const updatedPreferences = {
        ...watchlistColumnPreferences,
        [watchlistId]: presetView
      };
      setWatchlistColumnPreferences(updatedPreferences);
      localStorage.setItem(`watchlist_column_prefs_${profileId}`, JSON.stringify(updatedPreferences));
      console.log('Saved column preference:', { watchlistId, presetView });
    } catch (err) {
      console.error('Error saving watchlist preferences:', err);
    }
  }, [watchlistColumnPreferences, profileId]);

  // Load preferences when the component mounts
  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  // Also load preferences when profile ID changes
  useEffect(() => {
    loadPreferences();
  }, [profileId, loadPreferences]);

  // Update the time horizon change handler
  const handleTimeHorizonChange = useCallback((watchlistId: string, horizon: TimeHorizon) => {
    setWatchlistTimeHorizons(prev => {
      const updated = {
        ...prev,
        [watchlistId]: horizon
      };
      // Save to localStorage
      localStorage.setItem(`watchlist_time_horizons_${profileId}`, JSON.stringify(updated));
      return updated;
    });
  }, [profileId]);

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Watchlists</Typography>
        <Button 
          variant="contained" 
          onClick={() => setIsCreateDialogOpen(true)}
          disabled={watchlists.length >= 10}
          startIcon={<Add />}
        >
          Create Watchlist
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <CircularProgress />}

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(800px, 1fr))',
        gap: 2
      }}>
        {watchlists.map((watchlist) => {
          // Add defensive check to ensure tickers exists and is an array
          const tickers = Array.isArray(watchlist.tickers) ? watchlist.tickers : [];
          
          // Safely map ticker data
          const wlt = tickers
            .map((ticker: string) => tickerData[ticker])
            .filter((ticker: any) => ticker !== undefined);
          
          // Get the saved column preference for this watchlist, or use default
          const savedPresetView = watchlistColumnPreferences[watchlist.id] || 'Default View';
          
          return (
            <EnhancedWatchlistTable
              key={`enhanced-watchlist-${watchlist.id}`}
              title={watchlist.name || 'Untitled Watchlist'}
              stocks={wlt}
              onTickerSelect={handleTickerSelect}
              actions={getActions(watchlist)}
              timeHorizon={watchlistTimeHorizons[watchlist.id] || 'Short Term'}
              onTimeHorizonChange={(horizon: TimeHorizon) => handleTimeHorizonChange(watchlist.id, horizon)}
              initialPresetView={savedPresetView}
              onPresetViewChange={(presetView: string) => {
                saveColumnPreference(watchlist.id, presetView);
              }}
            />
          );
        })}
      </Box>

      {/* Create Watchlist Dialog */}
      <Dialog open={isCreateDialogOpen} onClose={() => {
        setIsCreateDialogOpen(false);
        setCreateError(null);
        setNewWatchlistName('');
        setNewWatchlistTickers('');
        setInvalidTickers([]);
        setHasAttemptedSubmit(false);
      }}>
        <DialogTitle>Create New Watchlist</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          <TextField
            autoFocus
            margin="dense"
            label="Watchlist Name"
            fullWidth
            value={newWatchlistName}
            onChange={(e) => setNewWatchlistName(e.target.value)}
            error={hasAttemptedSubmit && !newWatchlistName.trim()}
            helperText={hasAttemptedSubmit && !newWatchlistName.trim() ? "Watchlist name is required" : ""}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Initial Tickers (comma-separated)"
            fullWidth
            value={newWatchlistTickers}
            onChange={(e) => {
              setNewWatchlistTickers(e.target.value.toUpperCase());
              
              // Clear invalid tickers when input changes
              if (invalidTickers.length > 0) {
                setInvalidTickers([]);
                setCreateError(null);
              }
            }}
            helperText={
              createError ? createError : 
              invalidTickers.length > 0 ? `Invalid ticker(s): ${invalidTickers.join(', ')}` : 
              "Example: AAPL, MSFT, GOOGL"
            }
            error={createError !== null || invalidTickers.length > 0 || (hasAttemptedSubmit && !newWatchlistTickers.trim())}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setIsCreateDialogOpen(false);
            setCreateError(null);
            setNewWatchlistName('');
            setNewWatchlistTickers('');
            setInvalidTickers([]);
            setHasAttemptedSubmit(false);
          }} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setHasAttemptedSubmit(true);
              handleCreateWatchlist();
            }}
            variant="contained" 
            disabled={loading || isValidatingTickers}
          >
            {loading || isValidatingTickers ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Watchlist Dialog */}
      <Dialog 
        open={isEditDialogOpen} 
        onClose={() => {
          // Reset all state when dialog is closed
          setIsEditDialogOpen(false);
          setSelectedWatchlist(null);
          setEditWatchlistName('');
          setEditWatchlistTickers('');
          setEditError(null);
          setInvalidTickers([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Watchlist</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          
          <TextField
            autoFocus
            margin="dense"
            label="Watchlist Name"
            fullWidth
            value={editWatchlistName || (selectedWatchlist?.name || '')}
            onChange={(e) => {
              setEditWatchlistName(e.target.value);
              if (!e.target.value || e.target.value.trim() === '') {
                setEditError('Please enter a watchlist name');
              } else {
                setEditError(null);
              }
            }}
            error={!editWatchlistName && !selectedWatchlist?.name}
            helperText={(!editWatchlistName && !selectedWatchlist?.name) ? 'Watchlist name is required' : ''}
            sx={{ mb: 3 }}
          />
          
          <Typography variant="subtitle1" gutterBottom>Manage Tickers</Typography>
          
          {/* Current tickers display */}
          {editWatchlistTickers && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Current tickers:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {(editWatchlistTickers || '').split(',').map((ticker, index) => {
                  const trimmedTicker = ticker?.trim();
                  if (!trimmedTicker) return null;
                  
                  return (
                    <Chip 
                      key={`${trimmedTicker}-${index}`}
                      label={trimmedTicker.toUpperCase()}
                      onDelete={() => handleRemoveTicker(trimmedTicker)}
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                  );
                })}
              </Stack>
            </Box>
          )}
          
          <TextField
            margin="dense"
            label="Edit Tickers (comma-separated)"
            fullWidth
            value={editWatchlistTickers || ''}
            onChange={(e) => {
              // Convert to uppercase as typed
              const uppercaseValue = e.target.value.toUpperCase();
              
              // Always update the input value
              setEditWatchlistTickers(uppercaseValue);
              
              // Explicitly clear error state whenever the input changes
              if (invalidTickers.length > 0 || editError) {
                setInvalidTickers([]);
                setEditError(null);
              }
            }}
            helperText={
              editError ? editError : 
              invalidTickers.length > 0 ? `Invalid ticker(s): ${invalidTickers.join(', ')}` : 
              "Example: AAPL, MSFT, GOOGL"
            }
            error={editError !== null || invalidTickers.length > 0}
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            // Reset all errors and state when canceling
            setIsEditDialogOpen(false);
            setSelectedWatchlist(null);
            setEditWatchlistName('');
            setEditWatchlistTickers('');
            setEditError(null);
            setInvalidTickers([]);
          }} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateWatchlist} 
            variant="contained" 
            disabled={
              loading || 
              isValidatingTickers ||
              (editWatchlistTickers.trim() === '') ||
              invalidTickers.length > 0
            }
          >
            {loading || isValidatingTickers ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDeleteDialogOpen}
        title="Delete Watchlist"
        message="Are you sure you want to delete this watchlist?"
        onConfirm={handleDeleteWatchlist}
        onCancel={() => setConfirmDeleteDialogOpen(false)}
      />

      {/* Ticker Detail Dialog */}
      <Dialog
        open={!!selectedTicker}
        maxWidth="md"
        fullWidth
        onClose={() => setSelectedTicker(null)}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <IconButton
            aria-label="close"
            onClick={() => setSelectedTicker(null)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {tickerDetailData && (
            <TickerDetailView 
              tickerInfo={tickerDetailData}
              ticker={selectedTicker!}
              onBack={() => setSelectedTicker(null)}
            />
          )}
        </DialogContent>
      </Dialog>

    </Box>
  );
};
